import { useState, useCallback, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { getOrCreateDeviceId, parseUserAgent } from '../lib/sessionDevice';

export interface DeviceSession {
  id: string;
  device_id: string;
  ip_address: string | null;
  device_type: string;
  os_name: string;
  browser_name: string;
  session_label: string | null;
  last_active_at: string;
  created_at: string;
  is_revoked: boolean;
  isCurrent: boolean;
}

export function useSessionDevices() {
  const { user } = useAuth();
  const [sessions, setSessions] = useState<DeviceSession[]>([]);
  const [loading, setLoading] = useState(false);
  const [revoking, setRevoking] = useState<string | null>(null);

  const currentDeviceId = getOrCreateDeviceId();

  const fetchSessions = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('user_sessions')
        .select('*')
        .eq('user_id', user.id)
        .eq('is_revoked', false)
        .order('last_active_at', { ascending: false });

      if (error) {
        console.warn('[useSessionDevices] fetch error:', error.message);
        return;
      }

      const mapped: DeviceSession[] = (data ?? []).map(row => ({
        id: row.id,
        device_id: row.device_id,
        ip_address: row.ip_address,
        device_type: row.device_type ?? parseUserAgent(row.user_agent ?? '').deviceType,
        os_name: row.os_name ?? parseUserAgent(row.user_agent ?? '').osName,
        browser_name: row.browser_name ?? parseUserAgent(row.user_agent ?? '').browserName,
        session_label: row.session_label,
        last_active_at: row.last_active_at,
        created_at: row.created_at,
        is_revoked: row.is_revoked,
        isCurrent: row.device_id === currentDeviceId,
      }));

      // Put current device first
      mapped.sort((a, b) => (b.isCurrent ? 1 : 0) - (a.isCurrent ? 1 : 0));
      setSessions(mapped);
    } finally {
      setLoading(false);
    }
  }, [user, currentDeviceId]);

  useEffect(() => {
    fetchSessions();
  }, [fetchSessions]);

  const revokeSession = useCallback(async (sessionId: string) => {
    if (!user) return;
    setRevoking(sessionId);
    try {
      const { error } = await supabase
        .from('user_sessions')
        .update({ is_revoked: true, revoked_at: new Date().toISOString() })
        .eq('id', sessionId)
        .eq('user_id', user.id);

      if (!error) {
        setSessions(prev => prev.filter(s => s.id !== sessionId));
      }
    } finally {
      setRevoking(null);
    }
  }, [user]);

  const revokeAllOthers = useCallback(async () => {
    if (!user) return;
    setRevoking('all');
    try {
      const { error } = await supabase
        .from('user_sessions')
        .update({ is_revoked: true, revoked_at: new Date().toISOString() })
        .eq('user_id', user.id)
        .neq('device_id', currentDeviceId);

      if (!error) {
        setSessions(prev => prev.filter(s => s.isCurrent));
      }
    } finally {
      setRevoking(null);
    }
  }, [user, currentDeviceId]);

  return {
    sessions,
    loading,
    revoking,
    fetchSessions,
    revokeSession,
    revokeAllOthers,
  };
}
